import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Chip,
  Icon,
  ListGroup,
  ListRow,
  SegmentedControl,
  ServerCard,
  Skeleton,
  Switch,
  TextField,
} from '@uplink/ui';
import { useEffect, useRef, useState } from 'react';
import { errorText, useTRPC } from '../api.ts';
import { MonitoringSection } from '../monitoring.tsx';
import { MainActionBar } from '../native-controls.tsx';
import type { Nav } from '../nav.ts';
import { usePrefs } from '../prefs.tsx';
import { ConfirmButton, CopyText, serverState } from '../servers-ui.tsx';
import { haptic } from '../telegram.ts';
import { VpnSection } from '../vpn.tsx';

type Os = 'windows' | 'mac' | 'linux' | 'phone';

/** The visitor's system, to open the right tab of the guide first. */
function guessOs(): Os {
  const ua = navigator.userAgent;
  if (/android|iphone|ipad/i.test(ua)) return 'phone';
  if (/mac/i.test(ua)) return 'mac';
  if (/linux/i.test(ua)) return 'linux';
  return 'windows';
}

/** Step by step SSH for people who have never done it, per system. */
function ConnectGuide({ command, initiallyOpen }: { command: string; initiallyOpen: boolean }) {
  const { t } = usePrefs();
  const [open, setOpen] = useState(initiallyOpen);
  const [os, setOs] = useState<Os>(guessOs);
  if (!open) {
    return (
      <ListGroup>
        <ListRow icon="info" title={t.server.guide} chevron onClick={() => setOpen(true)} />
      </ListGroup>
    );
  }
  return (
    <section className="ul-stack" aria-labelledby="srv-guide">
      <h2 className="ul-group__header" id="srv-guide">
        {t.server.guide}
      </h2>
      <SegmentedControl<Os>
        label={t.server.guide}
        value={os}
        onChange={setOs}
        options={(['windows', 'mac', 'linux', 'phone'] as Os[]).map((o) => ({
          value: o,
          label: t.server.guideOs[o],
        }))}
      />
      <ol className="ul-guide">
        {t.server.guideSteps[os].map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
      {os !== 'phone' ? (
        <ListGroup footer={t.server.guideFooter}>
          <ListRow
            icon="terminal"
            title={<span className="ul-mono">{command}</span>}
            trailing={<CopyText text={command} />}
          />
        </ListGroup>
      ) : null}
    </section>
  );
}

export function ServerScreen({ nav, id }: { nav: Nav; id: string }) {
  const trpc = useTRPC();
  const { t, money, date } = usePrefs();
  const queryClient = useQueryClient();
  const server = useQuery({
    ...trpc.servers.get.queryOptions({ id }),
    refetchInterval: (q) => {
      const s = q.state.data;
      if (!s) return false;
      const settling = s.status === 'pending' || s.status === 'provisioning';
      const busy = s.status === 'active' && s.powerState !== 'running' && s.powerState !== 'stopped';
      return settling || busy ? 3000 : false;
    },
  });
  const images = useQuery(trpc.servers.images.queryOptions());
  const balance = useQuery(trpc.balance.summary.queryOptions());

  const refresh = () => {
    queryClient.invalidateQueries(trpc.servers.pathFilter());
    queryClient.invalidateQueries(trpc.balance.pathFilter());
  };
  const opts = { onSuccess: refresh, onError: () => haptic.error() };
  const power = useMutation(trpc.servers.power.mutationOptions(opts));
  const password = useMutation(trpc.servers.password.mutationOptions({ onError: () => haptic.error() }));
  const resetPassword = useMutation(
    trpc.servers.resetPassword.mutationOptions({
      onSuccess: () => {
        haptic.success();
        password.reset();
      },
      onError: () => haptic.error(),
    }),
  );
  const reinstall = useMutation(trpc.servers.reinstall.mutationOptions(opts));
  const renew = useMutation(
    trpc.servers.renew.mutationOptions({
      ...opts,
      onSuccess: () => {
        haptic.success();
        refresh();
      },
    }),
  );
  const autoRenew = useMutation(trpc.servers.setAutoRenew.mutationOptions(opts));
  const rename = useMutation(
    trpc.servers.rename.mutationOptions({
      onSuccess: () => {
        haptic.success();
        setEditingName(false);
        refresh();
      },
      onError: () => haptic.error(),
    }),
  );
  const remove = useMutation(
    trpc.servers.delete.mutationOptions({
      onSuccess: () => {
        haptic.success();
        refresh();
        nav.back();
      },
      onError: () => haptic.error(),
    }),
  );
  const [reinstallImage, setReinstallImage] = useState<string | null>(null);
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState('');

  const s = server.data;
  // Seen being created and now up: celebrate and open the guide
  const wasSettling = useRef(false);
  const [justReady, setJustReady] = useState(false);
  const status = server.data?.status;
  useEffect(() => {
    if (status === 'pending' || status === 'provisioning') wasSettling.current = true;
    else if (status === 'active' && wasSettling.current) {
      wasSettling.current = false;
      setJustReady(true);
      haptic.success();
    }
  }, [status]);

  if (server.isPending) {
    return (
      <main className="ul-screen">
        <Skeleton height={120} />
        <Skeleton height={200} />
      </main>
    );
  }
  if (!s) {
    return (
      <main className="ul-screen">
        <div className="ul-error" role="alert">
          {errorText(server.error)}
        </div>
      </main>
    );
  }

  const st = serverState(s, t);
  const settling = s.status === 'pending' || s.status === 'provisioning';
  const suspended = s.status === 'suspended';
  const error = [power, password, resetPassword, reinstall, renew, autoRenew, rename, remove].find(
    (m) => m.isError,
  )?.error;
  const shortBy = balance.data ? Math.max(0, s.priceCents - balance.data.balanceCents) : 0;
  const sshCommand = s.ipv4 ? `ssh root@${s.ipv4}` : null;

  if (s.status === 'failed') {
    return (
      <main className="ul-screen ul-screen--sections">
        <ServerCard
          name={s.name}
          state={st.state}
          region={s.city}
          country={s.country}
          specs={[s.spec, s.imageName, st.label].filter(Boolean)}
        />
        <section className="ul-stack">
          <div className="ul-error" role="alert">
            <strong>{t.server.failedTitle}</strong>
            <br />
            {t.server.failedText}
            {s.failureReason ? (
              <>
                <br />
                <span className="ul-mono">{s.failureReason}</span>
              </>
            ) : null}
          </div>
          {s.refunded ? (
            <div className="ul-success-note" role="status">
              {t.server.refunded}
            </div>
          ) : null}
        </section>
      </main>
    );
  }

  return (
    <main className={`ul-screen ul-screen--sections${suspended ? ' ul-screen--with-action' : ''}`}>
      <ServerCard
        name={s.name}
        state={st.state}
        region={s.city}
        country={s.country}
        specs={[s.spec, s.imageName, st.state === 'ok' ? '' : st.label].filter(Boolean)}
        ip={s.ipv4 ?? undefined}
        onCopy={() => haptic.success()}
      />

      {editingName ? (
        <section className="ul-stack" aria-labelledby="server-name-title">
          <h2 className="ul-group__header" id="server-name-title">
            {t.server.name}
          </h2>
          <TextField
            id="server-name"
            label={t.server.name}
            value={name}
            onChange={setName}
            placeholder={t.server.namePlaceholder}
            error={rename.isError ? errorText(rename.error) : undefined}
          />
          <div className="ul-row-actions">
            <Button
              variant="secondary"
              onClick={() => {
                rename.reset();
                setEditingName(false);
              }}
            >
              {t.common.cancel}
            </Button>
            <Button
              loading={rename.isPending}
              disabled={!name.trim() || name.trim() === s.name}
              onClick={() => rename.mutate({ id, name: name.trim() })}
            >
              {t.common.save}
            </Button>
          </div>
        </section>
      ) : (
        <ListGroup>
          <ListRow
            icon="edit"
            title={t.server.name}
            subtitle={s.name}
            chevron
            onClick={() => {
              setName(s.name);
              setEditingName(true);
            }}
          />
        </ListGroup>
      )}

      {settling ? (
        <section className="ul-group" aria-label={t.server.creating}>
          <ol className="ul-tl" aria-live="polite">
            {[
              { label: t.server.stepPaid, done: true },
              {
                label: t.server.stepCreate,
                done: s.status === 'provisioning',
                active: s.status === 'pending',
              },
              { label: t.server.stepInstall(s.imageName), done: false, active: s.status === 'provisioning' },
              ...(s.vpn ? [{ label: t.server.stepVpn, done: false }] : []),
              { label: t.server.stepReady, done: false },
            ].map((step) => (
              <li
                key={step.label}
                className={`ul-tl__step${step.done ? ' is-done' : ''}${step.active ? ' is-active' : ''}`}
              >
                <span className="ul-tl__dot" aria-hidden>
                  {step.done ? <Icon name="check" size={14} /> : null}
                </span>
                <span className="ul-tl__label">{step.label}</span>
              </li>
            ))}
          </ol>
          <div className="ul-group__footer" style={{ padding: '0 16px 16px' }}>
            {t.server.creatingFooter}
          </div>
        </section>
      ) : null}

      {justReady ? (
        <div className="ul-ready" role="status">
          <span className="ul-ready__mark" aria-hidden>
            <Icon name="rocket" size={28} weight="fill" />
          </span>
          <span className="ul-ready__title">{t.server.readyTitle}</span>
          <span className="ul-ready__text">{t.server.readyText}</span>
        </div>
      ) : null}

      {!settling && s.status !== 'deleted' ? (
        <>
          <ListGroup
            header={t.server.access}
            footer={
              <>
                {t.server.accessFooter}{' '}
                <button
                  type="button"
                  className="ul-link"
                  disabled={resetPassword.isPending}
                  onClick={() => resetPassword.mutate({ id })}
                >
                  {resetPassword.isSuccess ? t.server.passwordReset : t.server.resetPassword}
                </button>
              </>
            }
          >
            {sshCommand ? (
              <ListRow
                icon="terminal"
                title={t.server.connect}
                subtitle={<span className="ul-mono">{sshCommand}</span>}
                trailing={<CopyText text={sshCommand} label="" />}
              />
            ) : null}
            {s.ipv6 ? (
              <ListRow
                icon="globe"
                title="IPv6"
                subtitle={<span className="ul-mono">{s.ipv6}</span>}
                trailing={<CopyText text={s.ipv6} label="" />}
              />
            ) : null}
            {password.data ? (
              <ListRow
                icon="key"
                title={t.server.rootPassword}
                subtitle={<span className="ul-mono">{password.data.password}</span>}
                trailing={<CopyText text={password.data.password} label="" />}
              />
            ) : (
              <ListRow
                icon="key"
                title={t.server.rootPassword}
                subtitle={<span className="ul-mono">••••••••••••</span>}
                trailing={
                  <Button
                    variant="ghost"
                    loading={password.isPending}
                    onClick={() => password.mutate({ id })}
                  >
                    {t.common.show}
                  </Button>
                }
              />
            )}
          </ListGroup>

          {sshCommand ? <ConnectGuide command={sshCommand} initiallyOpen={justReady} /> : null}

          <VpnSection
            id={id}
            active={s.status === 'active' && !suspended}
            reinstall={(() => {
              const target = images.data?.find(
                (candidate) =>
                  candidate.provider === s.provider &&
                  candidate.vpnSupported &&
                  (!s.images || s.images.includes(candidate.id)),
              );
              return target
                ? {
                    imageName: target.name,
                    loading: reinstall.isPending,
                    onConfirm: () => reinstall.mutate({ id, imageId: target.id }),
                  }
                : undefined;
            })()}
          />

          {!suspended ? <MonitoringSection id={id} /> : null}

          {!suspended ? (
            <section className="ul-stack" aria-labelledby="srv-power">
              <h2 className="ul-group__header" id="srv-power">
                {t.server.power}
              </h2>
              <div className="ul-row-actions">
                <Button
                  variant="secondary"
                  icon="reboot"
                  loading={power.isPending && power.variables?.action === 'reboot'}
                  disabled={s.powerState !== 'running'}
                  onClick={() => power.mutate({ id, action: 'reboot' })}
                >
                  {t.server.reboot}
                </Button>
                {s.powerState === 'stopped' ? (
                  <Button
                    variant="secondary"
                    icon="power"
                    loading={power.isPending && power.variables?.action === 'start'}
                    onClick={() => power.mutate({ id, action: 'start' })}
                  >
                    {t.server.start}
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    icon="power"
                    loading={power.isPending && power.variables?.action === 'shutdown'}
                    disabled={s.powerState !== 'running'}
                    onClick={() => power.mutate({ id, action: 'shutdown' })}
                  >
                    {t.server.shutdown}
                  </Button>
                )}
              </div>
            </section>
          ) : null}

          <ListGroup
            header={t.server.billing}
            footer={
              suspended ? t.server.suspendedFooter : s.autoRenew ? t.server.autoFooter : t.server.manualFooter
            }
          >
            <ListRow icon="clock" title={t.server.paidUntil} value={s.paidUntil ? date(s.paidUntil) : '—'} />
            <ListRow
              icon="wallet"
              title={t.common.autoRenew}
              subtitle={t.server.perMonth(money(s.priceCents))}
              trailing={
                <Switch
                  label={t.common.autoRenew}
                  checked={s.autoRenew}
                  disabled={autoRenew.isPending}
                  onChange={(on) => {
                    haptic.select();
                    autoRenew.mutate({ id, autoRenew: on });
                  }}
                />
              }
            />
          </ListGroup>
          {!suspended ? (
            shortBy > 0 ? (
              <Button
                variant="secondary"
                block
                icon="plus"
                onClick={() =>
                  nav.push({
                    name: 'topup',
                    amountCents: Math.max(shortBy, 500),
                    after: { kind: 'serverRenew', id, label: s.name },
                  })
                }
              >
                {t.server.renewShort(money(shortBy))}
              </Button>
            ) : (
              <Button
                variant="secondary"
                block
                loading={renew.isPending}
                onClick={() => renew.mutate({ id })}
              >
                {t.server.renewFor(money(s.priceCents))}
              </Button>
            )
          ) : null}

          {!suspended && s.reinstallSupported && images.data ? (
            <section className="ul-stack" aria-labelledby="srv-reinstall">
              <h2 className="ul-group__header" id="srv-reinstall">
                {t.server.reinstall}
              </h2>
              <div className="ul-chips">
                {images.data
                  .filter((i) => i.provider === s.provider && (!s.images || s.images.includes(i.id)))
                  .map((i) => (
                    <Chip
                      key={i.id}
                      selected={i.id === (reinstallImage ?? '')}
                      onClick={() => {
                        haptic.select();
                        setReinstallImage(i.id);
                      }}
                    >
                      {i.name}
                    </Chip>
                  ))}
              </div>
              {reinstallImage ? (
                <ConfirmButton
                  variant="secondary"
                  loading={reinstall.isPending}
                  confirmText={t.server.reinstallConfirm}
                  onConfirm={() =>
                    reinstall.mutate(
                      { id, imageId: reinstallImage },
                      { onSuccess: () => setReinstallImage(null) },
                    )
                  }
                >
                  {t.server.reinstallTo(images.data.find((i) => i.id === reinstallImage)?.name ?? '')}
                </ConfirmButton>
              ) : null}
            </section>
          ) : null}

          <section className="ul-stack" aria-labelledby="srv-danger">
            <h2 className="ul-group__header" id="srv-danger">
              {t.server.danger}
            </h2>
            <ConfirmButton
              loading={remove.isPending}
              confirmText={t.server.deleteConfirm}
              onConfirm={() => remove.mutate({ id })}
            >
              {t.server.delete}
            </ConfirmButton>
            <div className="ul-group__footer">{t.server.deleteFooter}</div>
          </section>
        </>
      ) : null}

      {error ? (
        <div className="ul-error" role="alert">
          {errorText(error)}
        </div>
      ) : null}

      {suspended ? (
        <MainActionBar
          action={
            shortBy > 0
              ? {
                  text: t.common.topUpBy(money(Math.max(shortBy, 500))),
                  onClick: () =>
                    nav.push({
                      name: 'topup',
                      amountCents: Math.max(shortBy, 500),
                      after: { kind: 'serverRenew', id, label: s.name },
                    }),
                }
              : {
                  text: t.server.payAndStart(money(s.priceCents)),
                  onClick: () => renew.mutate({ id }),
                  loading: renew.isPending,
                }
          }
        />
      ) : null}
    </main>
  );
}
